// Central loader for DB-managed pricing. Falls back to these baked-in defaults
// (identical to the original hard-coded values) if the backend is unreachable,
// so the live calculator can never break.
import { useEffect, useState } from 'react';
import axios from 'axios';
import { CALC_CONFIGS } from './calculatorConfigs';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const DEFAULT_PEST_CARDS = [
  { id: 'general', icon: '🏠', label: 'General Pest', rate: 3 },
  { id: 'cockroach', icon: '🪳', label: 'Cockroach', rate: 3.5 },
  { id: 'termite', icon: '🛡️', label: 'Termite', rate: 6 },
  { id: 'bedbug', icon: '🛏️', label: 'Bed Bug', rate: 5 },
  { id: 'rodent', icon: '🐀', label: 'Rodent', rate: 3 },
  { id: 'mosquito', icon: '⚡', label: 'Mosquito', rate: 4 },
  { id: 'ant', icon: '🐜', label: 'Ant', rate: 3 },
  { id: 'lizard', icon: '🦎', label: 'Lizard', rate: 3 },
  { id: 'spider', icon: '🕷️', label: 'Spider', rate: 3 },
  { id: 'flea', icon: '🦟', label: 'Flea', rate: 3.5 },
  { id: 'silverfish', icon: '🐛', label: 'Silverfish', rate: 3 },
  { id: 'fly', icon: '🪰', label: 'Fly', rate: 3 },
  { id: 'other', icon: 'ℹ️', label: 'Other / Not Sure', rate: 0 },
];

export const DEFAULT_BHK_OPTIONS = [
  { value: '1bhk', label: '1 BHK', area: 500 },
  { value: '2bhk', label: '2 BHK', area: 800 },
  { value: '3bhk', label: '3 BHK', area: 1200 },
  { value: '4bhk', label: '4 BHK', area: 1600 },
  { value: '5bhk', label: '5 BHK', area: 2000 },
];

export const DEFAULT_GLOBAL_MIN = 799;
export const DEFAULT_ROUND_TO = 100;

const COMMERCIAL_LABELS = [
  'Restaurants & Cafés', 'Hotels & Resorts', 'Offices & Coworking', 'Hospitals & Clinics',
  'Schools & Colleges', 'Retail Stores', 'Shopping Malls', 'Cloud Kitchens', 'Banks & ATMs',
  'Pharmacies', 'Salons & Spas', 'Gyms & Fitness', 'Cinemas & Theatres', 'Warehouses',
  'Food Processing', 'Logistics & Couriers', 'Hostels & PGs', 'Cafés & Bakeries',
  'Pet Clinics', 'Event Halls & Banquets', 'Other / Not Listed',
];
const INDUSTRIAL_LABELS = [
  'Manufacturing Plants', 'Warehouses & Godowns', 'Cold Storage', 'Food Processing Units',
  'Pharmaceutical Plants', 'Textile Mills', 'Chemical Plants', 'Automotive Plants',
  'Steel & Metal Plants', 'Cement Plants', 'Power Plants', 'Oil & Gas Refineries',
  'Paper & Pulp Mills', 'Plastic & Rubber Units', 'Distilleries & Breweries',
  'Electronics & Semiconductor', 'Dairy & Cattle Farms', 'Grain Silos & Storage',
  'Logistics & Distribution', 'Shipping & Ports', 'Other / Not Listed',
];

export const DEFAULT_COMMERCIAL = {
  min_price: 1500,
  types: COMMERCIAL_LABELS.map((label) => ({ label, rate: label === 'Other / Not Listed' ? 0 : 5 })),
};
export const DEFAULT_INDUSTRIAL = {
  min_price: 3000,
  types: INDUSTRIAL_LABELS.map((label) => ({ label, rate: label === 'Other / Not Listed' ? 0 : 6 })),
};

const FALLBACK = {
  pests: DEFAULT_PEST_CARDS,
  bhkOptions: DEFAULT_BHK_OPTIONS,
  showBhkArea: true,
  globalMinPrice: DEFAULT_GLOBAL_MIN,
  roundTo: DEFAULT_ROUND_TO,
  services: CALC_CONFIGS,
  commercial: DEFAULT_COMMERCIAL,
  industrial: DEFAULT_INDUSTRIAL,
  areaLimits: { residential: 100000, commercial: 100000, industrial: 100000 },
};

let _cache = null;
let _inflight = null;

export function notifyPricingConfigUpdated(config = null) {
  _cache = config ? _normalize(config) : null;
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new Event('pricing-config-updated'));
  try { window.localStorage.setItem('pricing-config-updated-at', String(Date.now())); } catch (_) { /* Storage may be unavailable. */ }
}

function _normalize(data) {
  if (!data) return FALLBACK;
  return {
    pests: Array.isArray(data.pests) && data.pests.length ? data.pests : DEFAULT_PEST_CARDS,
    bhkOptions: Array.isArray(data.bhk_options) && data.bhk_options.length ? data.bhk_options : DEFAULT_BHK_OPTIONS,
    showBhkArea: data.show_bhk_area !== false,
    globalMinPrice: typeof data.global_min_price === 'number' ? data.global_min_price : DEFAULT_GLOBAL_MIN,
    roundTo: typeof data.round_to === 'number' && data.round_to > 0 ? data.round_to : DEFAULT_ROUND_TO,
    services: data.services && Object.keys(data.services).length ? data.services : CALC_CONFIGS,
    commercial: data.commercial && data.commercial.types?.length ? data.commercial : DEFAULT_COMMERCIAL,
    industrial: data.industrial && data.industrial.types?.length ? data.industrial : DEFAULT_INDUSTRIAL,
    areaLimits: {
      residential: Number(data.area_limits?.residential) || 100000,
      commercial: Number(data.area_limits?.commercial) || 100000,
      industrial: Number(data.area_limits?.industrial) || 100000,
    },
  };
}

export async function fetchPricingConfig(force = false) {
  if (_cache && !force) return _cache;
  if (_inflight) return _inflight;
  _inflight = axios
    .get(`${API_BASE}/pricing/config`)
    .then((res) => { _cache = _normalize(res.data); return _cache; })
    .catch(() => FALLBACK)
    .finally(() => { _inflight = null; });
  return _inflight;
}

// Returns pricing config, seeded with fallback so the UI renders instantly and
// then swaps to DB values once loaded.
export function usePricingConfig() {
  const [config, setConfig] = useState(FALLBACK);
  useEffect(() => {
    let active = true;
    const refresh = () => fetchPricingConfig(true).then((c) => { if (active) setConfig(c); });
    fetchPricingConfig().then((c) => { if (active) setConfig(c); });
    const onConfigUpdate = () => refresh();
    const onStorage = (event) => { if (event.key === 'pricing-config-updated-at') refresh(); };
    window.addEventListener('pricing-config-updated', onConfigUpdate);
    window.addEventListener('storage', onStorage);
    const timer = window.setInterval(refresh, 3000);
    return () => { active = false; window.clearInterval(timer); window.removeEventListener('pricing-config-updated', onConfigUpdate); window.removeEventListener('storage', onStorage); };
  }, []);
  return config;
}

// Per-service config (DB override merged over static), with fallback.
export function getServiceConfig(services, slug) {
  const src = services || CALC_CONFIGS;
  return src[slug] || CALC_CONFIGS[slug] || null;
}
